import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import { Header } from '../../../lib/header'
import { UserProfile } from '../../components/user-profile'
import { EditChargePointDialog } from '../../../common/dialogs/edit-charge-point-dialog'
import { UserProfileRfidTagsWidget } from '../../components/user-profile-rfid-tags-widget'
import { UserProfileLanguageSelect } from '../../components/user-profile-language-select'
import { UserProfileChargepointList } from '../../components/user-profile-chargepoint-list'
import { WidgetLoadingState } from '../../../lib/widget-loading-state'
import {
    useUserLoggedInUserIsCompanyMemberQuery,
    useUserGetOwnedChargePointsQuery,
    useUserRemoveChargePointAccessMutation,
    useUserGetOwnNfcTagsQuery,
    ChargePoint,
} from '../../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { RootState } from '../../redux/store'
import useText from '../../../common/hooks/useText'
import { confirm } from '../../../common/uikit/ConfirmDialog'
import ChargePointExpandOptions from '../../../lib/chargePointExpandOptions'

export function UserAppProfilePage(): React.ReactElement | null {
    const text = useText()
    const user = useSelector((state: RootState) => state.user)

    const [selectedChargePoint, setSelectedChargePoint] = useState<ChargePoint | null>(null)
    const { data: isCompanyMember, isLoading: loadingCompanyMember } = useUserLoggedInUserIsCompanyMemberQuery()

    const {
        data: userChargePoints,
        refetch,
        isLoading: chargePointLoading,
    } = useUserGetOwnedChargePointsQuery({ expand: ChargePointExpandOptions.OcppConfig })

    const [removeChargePoint] = useUserRemoveChargePointAccessMutation()
    const anyOcppEnabled = userChargePoints?.some((chargePoint) => chargePoint.ocppEnabled) ?? false

    const handleRemoveCharger = async (chargePointId: string) => {
        if (!chargePointId) return

        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('msgRemoveCharger'),
            variant: 'danger',
        })
        if (!confirmed) return

        try {
            await removeChargePoint({ chargePointId }).unwrap()
            notifySuccess(text('msgChargerWasSuccessfullyRemoved'))
        } catch {
            notifyError(text('msgChargerFailedToRemove'))
        }
    }

    const { data: tags, isLoading: loadingNfcTag } = useUserGetOwnNfcTagsQuery()
    const rfidTags = tags?.map((tag) => ({ tagId: tag.id ?? '', tagComment: tag.comment ?? '' })) ?? []

    if (!user) return null
    return (
        <div className="page-layout-container">
            <div className="row">
                <div className="col-xs-12">
                    <Header code="lblProfile" className="profilePage" size="lg" data-testid="header-profile" />
                </div>
                <div className="col-xs-12 col-md-6 margin-top-small">
                    <UserProfile user={user} />
                    {!loadingCompanyMember &&
                        !isCompanyMember &&
                        !anyOcppEnabled &&
                        (loadingNfcTag ? (
                            <WidgetLoadingState />
                        ) : (
                            <UserProfileRfidTagsWidget rfid1={rfidTags[0]} rfid2={rfidTags[1]} rfid3={rfidTags[2]} />
                        ))}
                </div>
                <div className="col-xs-12 col-md-6 margin-top-small">
                    {chargePointLoading ? (
                        <WidgetLoadingState height="162px" />
                    ) : (
                        <UserProfileChargepointList
                            chargePoints={userChargePoints}
                            removeChargePoint={handleRemoveCharger}
                            editChargePoint={setSelectedChargePoint}
                            loading={chargePointLoading}
                            user={user}
                        />
                    )}
                    <UserProfileLanguageSelect userId={user.id} />
                </div>
            </div>
            <EditChargePointDialog
                chargePoint={selectedChargePoint}
                visible={Boolean(selectedChargePoint)}
                onHide={() => setSelectedChargePoint(null)}
                onSaveSettled={() => refetch()}
                editChargerName
            />
        </div>
    )
}
